'use client';

import { c as _c } from "react/compiler-runtime";
import type { SearchedUser, SearchFactiii } from '@shared/all/domains/factiiis';
import { DeploymentLayout, type SearchedSpace } from '@shared/all/domains/spaces';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useInstanceSpaceSlug } from '../../hooks/useInstanceSpaceSlug';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import { spaceConfig } from '../../utils/getSpaceConfig';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Error from '../common/Error';
import ScrollableText from '../UI/ScrollableText';
type TagType = 'user' | 'space' | 'factiii';
type Props = {
  type: TagType;
  query: string;
  caretPosition: {
    top: number;
    left: number;
  } | null;
  closeHandler: () => void;
  itemClickHandler: (slug: string, id: number) => void;
  fullWidth?: boolean;
};
type ItemType = SearchedUser | SearchedSpace | SearchFactiii;
const TagSuggestionModal = props => {
  const $ = _c(60);
  const preferSpaceSlug = useInstanceSpaceSlug();
  let t0;
  if ($[0] !== preferSpaceSlug || $[1] !== props.query || $[2] !== props.type) {
    t0 = {
      queryKey: ["search", props.type, props.query, preferSpaceSlug],
      queryFn: async () => {
        if (props.type === "user") {
          return await unwrapFromResult(client.search.users.query({
            query: props.query,
            spaceIds: spaceConfig.layoutType === DeploymentLayout.Space ? [spaceConfig.space.id] : undefined
          }));
        } else {
          if (props.type === "factiii") {
            return await unwrapFromResult(client.search.factiiis.query({
              query: props.query,
              preferSpaceSlug
            }));
          } else {
            return await unwrapFromResult(client.search.spaces.query({
              query: props.query
            }));
          }
        }
      }
    };
    $[0] = preferSpaceSlug;
    $[1] = props.query;
    $[2] = props.type;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const {
    isLoading,
    isError,
    data
  } = useQuery(t0);
  let t1;
  if ($[4] !== data || $[5] !== props.type) {
    const getItems = () => {
      if (props.type === "user" && data && "users" in data) {
        return data.users ?? [];
      } else {
        if (props.type === "space" && data && "spaces" in data) {
          return data.spaces ?? [];
        } else {
          if (props.type === "factiii" && data && "factiiis" in data) {
            return data.factiiis ?? [];
          }
        }
      }
      return [];
    };
    t1 = getItems();
    $[4] = data;
    $[5] = props.type;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const items = t1;
  let t2;
  if ($[7] !== props) {
    t2 = item => {
      if (props.type === "user" && "username" in item) {
        props.itemClickHandler(item.username, item.id);
      } else {
        if (props.type === "space" && "spaceSlug" in item && item.spaceSlug) {
          props.itemClickHandler(item.spaceSlug, item.id);
        } else {
          if (props.type === "factiii" && "slug" in item) {
            props.itemClickHandler(item.slug, item.id);
          }
        }
      }
    };
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleSelect = t2;
  let t3;
  if ($[9] !== props.query) {
    t3 = [props.query];
    $[9] = props.query;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== handleSelect || $[12] !== items || $[13] !== props.closeHandler || $[14] !== t3) {
    t4 = {
      items,
      onSelect: handleSelect,
      onClose: props.closeHandler,
      resetDeps: t3
    };
    $[11] = handleSelect;
    $[12] = items;
    $[13] = props.closeHandler;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const {
    getItemProps
  } = useKeyboardNavigation(t4);
  let t5;
  if ($[16] !== props) {
    t5 = (slug, id) => {
      props.itemClickHandler(slug, id);
    };
    $[16] = props;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleItemClick = t5;
  let t6;
  if ($[18] !== getItemProps || $[19] !== handleItemClick) {
    t6 = (user, index) => {
      const {
        ref,
        onMouseEnter,
        isSelected
      } = getItemProps(index);
      return <div key={user.username} ref={ref} className={`flex cursor-pointer items-center space-x-4 rounded border border-ds-border-standard p-2 ${isSelected ? "bg-background-secondary" : "hover:bg-background-light"}`} onMouseDown={e => {
        e.preventDefault();
        handleItemClick(user.username, user.id);
      }} onMouseEnter={onMouseEnter}><Avatar avatarKey={user.avatarKey} robohash={user.robohash ?? ""} isBot={user.tag === "BOT"} size={40} userId={user.id} /><div className="min-w-0">{user.name ? <><ScrollableText className="font-semibold">{user.name}</ScrollableText><ScrollableText className="text-textSecondary/70">@{user.username}</ScrollableText></> : <><ScrollableText className="font-semibold">@{user.username}</ScrollableText></>}</div></div>;
    };
    $[18] = getItemProps;
    $[19] = handleItemClick;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const renderUserItem = t6;
  let t7;
  if ($[21] !== getItemProps || $[22] !== handleItemClick) {
    t7 = (space, index_0) => {
      const {
        ref: ref_0,
        onMouseEnter: onMouseEnter_0,
        isSelected: isSelected_0
      } = getItemProps(index_0);
      return <div key={space.spaceSlug} ref={ref_0} className={`flex cursor-pointer items-center space-x-4 rounded border border-ds-border-standard p-2 ${isSelected_0 ? "bg-background-secondary" : "hover:bg-background-light"}`} onMouseDown={e_0 => {
        e_0.preventDefault();
        handleItemClick(space.spaceSlug, space.id);
      }} onMouseEnter={onMouseEnter_0}><Avatar spaceAvatarKey={space.avatarKey} size={40} /><div className="min-w-0"><ScrollableText>{space.name}</ScrollableText><ScrollableText className="text-textSecondary/70">s/{space.spaceSlug}</ScrollableText></div></div>;
    };
    $[21] = getItemProps;
    $[22] = handleItemClick;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const renderSpaceItem = t7;
  let t8;
  if ($[24] !== getItemProps || $[25] !== handleItemClick) {
    t8 = (factiii, index_1) => {
      const {
        ref: ref_1,
        onMouseEnter: onMouseEnter_1,
        isSelected: isSelected_1
      } = getItemProps(index_1);
      return <div key={factiii.id} ref={ref_1} className={`flex cursor-pointer items-center space-x-4 rounded border border-ds-border-standard p-2 ${isSelected_1 ? "bg-background-secondary" : "hover:bg-background-light"}`} onMouseDown={e_1 => {
        e_1.preventDefault();
        handleItemClick(factiii.slug, factiii.id);
      }} onMouseEnter={onMouseEnter_1}><Avatar factiiiAvatarKey={factiii.avatarKey} factiiiId={factiii.id} size={40} /><div className="min-w-0"><ScrollableText>{factiii.name}</ScrollableText><div className=""><ScrollableText className="text-textSecondary/70">f/{factiii.slug} </ScrollableText><ScrollableText>{factiii.spaceSlug ? <>in{" "}<Link href={`/s/${factiii.spaceSlug}`}>{factiii.spaceSlug}</Link></> : <>by{" "}<Link href={`/user/${factiii.username}`}>{factiii.username}</Link></>}</ScrollableText></div></div></div>;
    };
    $[24] = getItemProps;
    $[25] = handleItemClick;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const renderFactiiiItem = t8;
  let t9;
  if ($[27] !== data || $[28] !== props.type || $[29] !== renderFactiiiItem || $[30] !== renderSpaceItem || $[31] !== renderUserItem) {
    t9 = () => {
      if (props.type === "user" && data && "users" in data) {
        if (data.users?.length === 0) {
          return <div className="py-4 text-center text-textSecondary">No users found</div>;
        }
        return data.users?.map((user_0, index_2) => renderUserItem(user_0, index_2));
      } else {
        if (props.type === "space" && data && "spaces" in data) {
          if (data.spaces?.length === 0) {
            return <div className="py-4 text-center text-textSecondary">No spaces found</div>;
          }
          return data.spaces?.map((space_0, index_3) => renderSpaceItem(space_0, index_3));
        } else {
          if (props.type === "factiii" && data && "factiiis" in data) {
            if (data.factiiis?.length === 0) {
              return <div className="py-4 text-center text-textSecondary">No factiiis found</div>;
            }
            return data.factiiis?.map((factiii_0, index_4) => renderFactiiiItem(factiii_0, index_4));
          }
        }
      }
      return null;
    };
    $[27] = data;
    $[28] = props.type;
    $[29] = renderFactiiiItem;
    $[30] = renderSpaceItem;
    $[31] = renderUserItem;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const renderContent = t9;
  if (props.fullWidth) {
    let t10;
    if ($[33] !== renderContent) {
      t10 = renderContent();
      $[33] = renderContent;
      $[34] = t10;
    } else {
      t10 = $[34];
    }
    let t11;
    if ($[35] !== isError || $[36] !== isLoading) {
      t11 = <Error error={isError} loading={isLoading} />;
      $[35] = isError;
      $[36] = isLoading;
      $[37] = t11;
    } else {
      t11 = $[37];
    }
    let t12;
    if ($[38] !== t10 || $[39] !== t11) {
      t12 = <div className="absolute inset-x-0 top-full z-50 mt-1 max-h-80 space-y-1 overflow-y-auto rounded-lg border border-ds-border-standard bg-background-primary p-2 text-textPrimary shadow-lg" onMouseDown={_temp}>{t10}{t11}</div>;
      $[38] = t10;
      $[39] = t11;
      $[40] = t12;
    } else {
      t12 = $[40];
    }
    return t12;
  }
  let t10;
  if ($[41] !== props.caretPosition) {
    t10 = props.caretPosition || {
      top: 0,
      left: 0
    };
    $[41] = props.caretPosition;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  const position = t10;
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1000;
  const clampedLeft = Math.min(position.left, viewportWidth - 288 - 16);
  const finalLeft = Math.max(16, clampedLeft);
  let t11;
  if ($[43] !== props) {
    t11 = <div className="fixed inset-0 z-popover bg-black opacity-50" onMouseDown={e_3 => {
      e_3.preventDefault();
      props.closeHandler();
    }} />;
    $[43] = props;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  const t12 = `${position.top}px`;
  const t13 = `${finalLeft}px`;
  let t14;
  if ($[45] !== t12 || $[46] !== t13) {
    t14 = {
      top: t12,
      left: t13
    };
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== renderContent) {
    t15 = renderContent();
    $[48] = renderContent;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== isError || $[51] !== isLoading) {
    t16 = <Error error={isError} loading={isLoading} />;
    $[50] = isError;
    $[51] = isLoading;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== t14 || $[54] !== t15 || $[55] !== t16) {
    t17 = <div className="fixed z-popover h-56 max-h-96 w-full max-w-72 space-y-1 overflow-y-auto rounded-lg bg-background-primary p-2 text-textPrimary shadow-lg" style={t14} onMouseDown={_temp2}>{t15}{t16}</div>;
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== t11 || $[58] !== t17) {
    t18 = <>{t11}{t17}</>;
    $[57] = t11;
    $[58] = t17;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  return t18;
};
export default TagSuggestionModal;
function _temp(e_2) {
  e_2.preventDefault();
}
function _temp2(e_4) {
  e_4.preventDefault();
}